// src/utils/api/instances.js

import axios from "axios";
import { getAuthHeadersData, getUserInfo, getTokenRefreshFunction, triggerLogout } from "./auth-bridge";
import { getSelectedTenantApiKey } from "../roles";
import { logger } from "../logger";
import i18n from "../../i18n/i18n";

// Embed mode: token comes from parent via postMessage, retry 401 via bridge
const isEmbedMode = new URLSearchParams(window.location.search).get("embed") === "true";

// Prevent multiple concurrent 401s from triggering repeated redirects
let isLoggingOut = false;

const getBaseUrl = (envVar, localUrl) => {
  return process.env[envVar] ? process.env[envVar] : localUrl;
};

export let relayBaseURL = getBaseUrl(
  "REACT_APP_RELAY_SERVER_URL",
  "ws://localhost:8081/"
);

// ============================================
// AXIOS INSTANCES
// ============================================

// Consolidated URL when auth is enabled (dev, test, imp, prod)
// Falls back to individual service URLs for local development
const consolidatedUrl = getBaseUrl("REACT_APP_API_URL");

const api = axios.create({
  baseURL: consolidatedUrl || getBaseUrl("REACT_APP_API_URL", "http://localhost:8002/"),
  headers: { Accept: "application/json" },
});

export const relayConfig = axios.create({
  baseURL: process.env.REACT_APP_RELAY_SERVER_URL,
  cognitiveApiKey: "",
});

// ============================================
// INTERCEPTORS: Request
// ============================================

/**
 * Request interceptor to set API key and auth token
 * @param {Object} config - Axios request config
 * @returns {Promise<Object>} Modified config
 */
async function setApiKey(config) {
  try {
    const apiConfig = sessionStorage.getItem("apiConfig");

    if (apiConfig) {
      const { apiKey, baseUrl, relayBaseURL: relayURL } = JSON.parse(apiConfig);

      if (apiKey) {
        config.headers["X-Api-Key"] = apiKey;
      }

      // Add Bearer token
      const { accessToken } = await getAuthHeadersData();
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }

      // Save config to sessionStorage for subsequent requests
      sessionStorage.setItem("apiConfig", JSON.stringify({ apiKey, baseUrl, relayBaseURL: relayURL || "" }));
    } else {
      // Fallback to getting API key from getUserInfo
      logger.warn("[API] No API config in sessionStorage, using fallback method.");

      const tenantApiKeyMap = await getUserInfo();
      const apiKey = getSelectedTenantApiKey(tenantApiKeyMap);

      if (apiKey) {
        config.headers["X-Api-Key"] = apiKey;
      }

      // Add Bearer token
      const { accessToken } = await getAuthHeadersData();
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }


    }

    if (!config.headers["X-Api-Key"]) {
      const { apiKey: envApiKey } = await getAuthHeadersData();
      if (envApiKey) {
        config.headers["X-Api-Key"] = envApiKey;
      }
    }
  } catch (e) {
    logger.error("[API] Error setting API key:", e);
  }

  // DEBUG: Verificar headers finales del request
  logger.debug('🚀 Request Debug:', {
    url: config.baseURL + config.url,
    method: config.method?.toUpperCase(),
    headers: {
      'X-Api-Key': config.headers['X-Api-Key'] ? '***' + config.headers['X-Api-Key'].slice(-4) : 'NOT SET',
      'Authorization': config.headers['Authorization'] ? config.headers['Authorization'].substring(0, 30) + '...' : 'NOT SET'
    }
  });

  return config;
}

// ============================================
// INTERCEPTORS: Response
// ============================================

/**
 * Check if the error is related to API key issues (not token issues)
 * @param {Object} error - Axios error object
 * @returns {boolean} True if it's an API key error
 */
const isApiKeyError = (error) => {
  const API_KEY_ERRORS = ["Missing X-API-Key header", "Invalid API key"];
  const errorMessage = error?.response?.data?.error || "";
  return API_KEY_ERRORS.some((msg) => errorMessage.includes(msg));
};

// Cognitive rejects every interaction with a disabled assistant with this exact message.
export const DISABLED_ASSISTANT_ERROR = "This assistant is disabled";

/**
 * Response interceptor to translate the disabled assistant rejection into a readable message
 * @param {Object} error - Axios error object
 * @returns {Promise<never>} Rejected promise
 */
const handleDisabledAssistantError = (error) => {
  if (
    error?.response?.status === 400 &&
    error?.response?.data?.error === DISABLED_ASSISTANT_ERROR
  ) {
    error.customMessage = i18n.t("assistantDisabled");
  }
  return Promise.reject(error);
};

/**
 * Response interceptor to handle auth errors
 * @param {Object} error - Axios error object
 * @returns {Promise<never>} Rejected promise
 */
const handleAuthError = async (error) => {
  const status = error?.response?.status;
  const url = error.config?.url;

  // DEBUG: Verificar respuesta de error
  if (status === 401 || status === 403) {
    logger.error('❌ Auth Error Debug:', {
      status,
      url,
      responseData: error?.response?.data,
      requestHeaders: {
        'X-Api-Key': error.config?.headers['X-Api-Key'] ? '***' + error.config.headers['X-Api-Key'].slice(-4) : 'NOT SET',
        'Authorization': error.config?.headers['Authorization'] ? error.config.headers['Authorization'].substring(0, 30) + '...' : 'NOT SET'
      }
    });
  }

  if (status === 401) {
    if (isApiKeyError(error)) {
      logger.warn("[API] 401 API Key Error:", url);
      error.customMessage = i18n.t("common.auth_api_key_error");
      return Promise.reject(error);
    }

    logger.error("[API] 401 Auth Error. Triggering logout:", url);

    // Embed mode only: ask parent for a fresh token via postMessage and retry once.
    // Does NOT use forceRefresh (B2C strips custom claims on forced refresh).
    if (isEmbedMode && !error.config?._retried) {
      error.config._retried = true;
      const refreshFn = getTokenRefreshFunction();
      if (refreshFn) {
        try {
          logger.debug("[API] 401 embed - requesting token refresh from parent");
          await refreshFn();
          return api(error.config);
        } catch (refreshErr) {
          logger.warn("[API] 401 embed - token refresh from parent failed:", refreshErr);
        }
      }
    }

    error.customMessage = i18n.t("common.auth_token_error");

    if (!isEmbedMode && !isLoggingOut) {
      isLoggingOut = true;
      setTimeout(() => {
        triggerLogout();
        isLoggingOut = false;
      }, 100);
    }

    return Promise.reject(error);
  }

  if (status === 403) {
    // 403 = authenticated but not authorized for this resource.
    // Re-authenticating won't grant new permissions, so do NOT trigger logout.
    logger.warn("[API] 403 Insufficient Permissions:", url);
    error.customMessage = i18n.t("common.auth_insufficient_permissions_error");
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

// ============================================
// APPLY INTERCEPTORS TO ALL INSTANCES
// ============================================

// Request interceptors
api.interceptors.request.use(setApiKey, (error) => Promise.reject(error));

// Response interceptors
api.interceptors.response.use(null, handleAuthError);
api.interceptors.response.use(null, handleDisabledAssistantError);

// ============================================
// Configure APIs by tenant
// ============================================

/**
 * Configure API URLs based on tenant
 * @param {string} tenant - The current tenant
 */
export function getConfiguredApiBaseUrl(tenant) {
  // Check if tenant exists and ends with "prod"
  return tenant && typeof tenant === "string" && tenant.toLowerCase().endsWith("prod")
    ? getBaseUrl("REACT_APP_API_URL_PROD")
    : getBaseUrl("REACT_APP_API_URL");
}

export function setApisURLs(tenant) {
  const baseUrl = getConfiguredApiBaseUrl(tenant);

  if (baseUrl) {
    api.defaults.baseURL = baseUrl;
  }
  // Otherwise, keep the individual configuration (local)
}

/**
 * Broadcast API configuration
 * @param {string} tenant - The current tenant
 */
export async function setApiConfig(tenant) {
  // Configure relay URL based on tenant
  if (tenant && typeof tenant === "string" && tenant.toLowerCase().endsWith("prod")) {
    relayBaseURL = getBaseUrl("REACT_APP_RELAY_SERVER_URL_PROD");
  } else {
    relayBaseURL = getBaseUrl(
      "REACT_APP_RELAY_SERVER_URL",
      "ws://localhost:8081/"
    );
  }

  const tenantApiKeyMap = await getUserInfo();
  const apiKey = getSelectedTenantApiKey(tenantApiKeyMap) || process.env.REACT_APP_API_KEY;
  const baseUrl = api.defaults.baseURL;

  // Update relay config
  relayConfig.defaults.baseURL = relayBaseURL;
  relayConfig.defaults.cognitiveApiKey = apiKey;

  // Save configuration to sessionStorage
  sessionStorage.setItem("apiConfig", JSON.stringify({ apiKey, baseUrl, relayBaseURL }));
}

/**
 * Configure API for embed mode using data received from the parent via postMessage.
 * Skips getUserInfo/extractTenantsAndApiKeys (token may lack api_keys claim).
 * @param {string|null} apiKey - API key from the parent
 * @param {string} tenant - Tenant from the parent
 */
export function setEmbedApiConfig(apiKey, tenant) {
  setApisURLs(tenant);
  const baseUrl = api.defaults.baseURL;
  sessionStorage.setItem("apiConfig", JSON.stringify({ apiKey, baseUrl, relayBaseURL }));
}

export default api;
export { api };
